// src/pages/Deposits.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Toolbar,
  InputBase,
  ButtonGroup,
  TablePagination,
  Grid,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DownloadIcon from "@mui/icons-material/Download";

import {
  getDeposits,
  approveDeposit,
  rejectDeposit,
  getDepositById,
} from "../services/api";

/* helpers */
const safe = (v, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : v;

const statusColor = (s) => {
  const st = (s || "").toString().toLowerCase();
  if (st.includes("approved")) return "success";
  if (st.includes("rejected")) return "error";
  return "warning";
};

export default function Deposits() {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Proof / dialogs
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {id, action}
  const [actionBusy, setActionBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await getDeposits();
      const data = res?.data?.data ?? res?.data ?? res ?? [];
      setDeposits(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load deposits", e);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Failed to load deposits"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* filter + search */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return deposits.filter((d) => {
      const status = (d.status || "pending").toLowerCase();
      if (filterStatus !== "all") {
        if (filterStatus === "pending" && status !== "pending") return false;
        if (filterStatus === "approved" && status !== "approved") return false;
        if (filterStatus === "rejected" && status !== "rejected") return false;
      }
      if (!q) return true;

      // search across useful fields including rockstarId
      const userName = (d.user?.name || "").toString().toLowerCase();
      const userEmail = (d.user?.email || d.email || "")
        .toString()
        .toLowerCase();
      const userRockstar = (d.rockstarId || d.user?.rockstarId || "")
        .toString()
        .toLowerCase();
      const method = (d.method || "").toString().toLowerCase();
      const amount = (d.amount || "").toString().toLowerCase();

      return (
        userName.includes(q) ||
        userEmail.includes(q) ||
        userRockstar.includes(q) ||
        method.includes(q) ||
        amount.includes(q)
      );
    });
  }, [deposits, query, filterStatus]);

  const visible = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* approve/reject */
  const openConfirm = (d, action) => {
    setConfirmAction({ id: d.id || d._id, action });
  };
  const closeConfirm = () => setConfirmAction(null);

  const performAction = async () => {
    if (!confirmAction) return;
    setActionBusy(true);
    try {
      if (confirmAction.action === "approve") {
        await approveDeposit(confirmAction.id);
      } else {
        // you may want to prompt for notes in future; for now send empty notes
        await rejectDeposit(confirmAction.id, { notes: "" });
      }
      await load();
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || e?.message || "Action failed");
    } finally {
      setActionBusy(false);
      closeConfirm();
    }
  };

  /* proof viewer */
  const openViewer = async (d) => {
    setSelectedDeposit(d);
    // if deposit lacks proofUrl, fetch full deposit from server
    if ((!d.proofUrl || !d.proofPath) && typeof getDepositById === "function") {
      try {
        const res = await getDepositById(d.id || d._id);
        setSelectedDeposit(res?.data?.data ?? res?.data ?? d);
      } catch (e) {
        console.error("failed load deposit proof", e);
      }
    }
  };
  const closeViewer = () => setSelectedDeposit(null);

  return (
    // Top padding to avoid app bar overlap, horizontal padding kept
    <Box sx={{ pt: 8, px: 3 }}>
      {/* Center wrapper with a comfortable max width */}
      <Box sx={{ maxWidth: 1280, mx: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Deposits
        </Typography>

        {/* top controls */}
        <Paper sx={{ mb: 2, p: 1 }}>
          <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
            <Paper
              component="form"
              sx={{
                p: "2px 8px",
                display: "flex",
                alignItems: "center",
                width: 360,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.08)",
              }}
              onSubmit={(e) => e.preventDefault()}
            >
              <SearchIcon color="action" />
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search user, email, rockstar id, method..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </Paper>

            <ButtonGroup variant="outlined" size="small">
              <Button
                color={filterStatus === "all" ? "primary" : "inherit"}
                onClick={() => setFilterStatus("all")}
              >
                All
              </Button>
              <Button
                color={filterStatus === "pending" ? "warning" : "inherit"}
                onClick={() => setFilterStatus("pending")}
              >
                Pending
              </Button>
              <Button
                color={filterStatus === "approved" ? "success" : "inherit"}
                onClick={() => setFilterStatus("approved")}
              >
                Approved
              </Button>
              <Button
                color={filterStatus === "rejected" ? "error" : "inherit"}
                onClick={() => setFilterStatus("rejected")}
              >
                Rejected
              </Button>
            </ButtonGroup>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="Refresh">
              <IconButton onClick={load}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Paper>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {errorMsg && (
                <Box sx={{ p: 2 }}>
                  <Typography color="error">{errorMsg}</Typography>
                </Box>
              )}
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Rockstar ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          No deposits found
                        </TableCell>
                      </TableRow>
                    ) : (
                      visible.map((d, i) => {
                        const id = d.id || d._id || i;
                        const status = (d.status || "Pending").toString();
                        const rockstar =
                          d.rockstarId || d.user?.rockstarId || "—";
                        return (
                          <TableRow key={id} hover>
                            <TableCell>
                              {page * rowsPerPage + i + 1}
                            </TableCell>
                            <TableCell>
                              <Grid container direction="column">
                                <Grid item sx={{ fontWeight: 700 }}>
                                  {safe(
                                    d.user?.name ||
                                      d.user?.email ||
                                      d.email
                                  )}
                                </Grid>
                                <Grid
                                  item
                                  sx={{ fontSize: 12, color: "text.secondary" }}
                                >
                                  {safe(d.user?.email || d.email)}
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell>{safe(rockstar)}</TableCell>
                            <TableCell>{safe(d.amount)}</TableCell>
                            <TableCell>{safe(d.method)}</TableCell>
                            <TableCell>
                              <Chip
                                label={status}
                                color={statusColor(status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {d.createdAt
                                ? new Date(d.createdAt).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Approve">
                                <span>
                                  <IconButton
                                    color="success"
                                    size="small"
                                    onClick={() => openConfirm(d, "approve")}
                                    disabled={
                                      actionBusy ||
                                      status.toLowerCase() !== "pending"
                                    }
                                  >
                                    <CheckCircleOutlineIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <span>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() => openConfirm(d, "reject")}
                                    disabled={
                                      actionBusy ||
                                      status.toLowerCase() !== "pending"
                                    }
                                  >
                                    <CancelOutlinedIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View Proof">
                                <IconButton
                                  size="small"
                                  onClick={() => openViewer(d)}
                                >
                                  <VisibilityOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                showFirstButton
                showLastButton
              />
            </>
          )}
        </Paper>

        {/* Proof dialog */}
        <Dialog
          open={Boolean(selectedDeposit)}
          onClose={closeViewer}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Deposit Proof</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">User</Typography>
              <Typography variant="body2">
                {safe(
                  selectedDeposit?.user?.name ||
                    selectedDeposit?.user?.email ||
                    selectedDeposit?.email
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {safe(
                  selectedDeposit?.user?.email || selectedDeposit?.email
                )}
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Rockstar ID</Typography>
              <Typography variant="body2">
                {safe(
                  selectedDeposit?.rockstarId ||
                    selectedDeposit?.user?.rockstarId
                )}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Amount</Typography>
              <Typography variant="body2">
                {safe(selectedDeposit?.amount)}
              </Typography>
            </Box>

            {selectedDeposit?.proofUrl ? (
              /\.(jpg|jpeg|png|gif|webp)$/i.test(selectedDeposit.proofUrl) ? (
                <Box sx={{ textAlign: "center" }}>
                  <img
                    src={selectedDeposit.proofUrl}
                    alt="Proof"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      borderRadius: 8,
                    }}
                    onClick={() => setPreviewImg(selectedDeposit.proofUrl)}
                  />
                </Box>
              ) : (
                <Typography>
                  File:{" "}
                  <a
                    href={selectedDeposit.proofUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {selectedDeposit.proofUrl}
                  </a>
                </Typography>
              )
            ) : (
              <Typography>No proof available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            {selectedDeposit?.proofUrl && (
              <>
                <Button
                  startIcon={<OpenInFullIcon />}
                  onClick={() => setPreviewImg(selectedDeposit.proofUrl)}
                >
                  Preview
                </Button>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = selectedDeposit.proofUrl;
                    a.download = "deposit-proof";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  }}
                >
                  Download
                </Button>
              </>
            )}
            <Button onClick={closeViewer}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Image preview */}
        <Dialog
          open={Boolean(previewImg)}
          onClose={() => setPreviewImg(null)}
          maxWidth="xl"
        >
          <img
            src={previewImg || ""}
            alt="preview"
            style={{ maxWidth: "90vw", maxHeight: "90vh" }}
          />
        </Dialog>

        {/* confirm dialog */}
        <Dialog open={Boolean(confirmAction)} onClose={closeConfirm}>
          <DialogTitle>
            {confirmAction?.action === "approve"
              ? "Approve Deposit"
              : "Reject Deposit"}
          </DialogTitle>
          <DialogContent dividers>
            <Typography>
              Are you sure you want to <strong>{confirmAction?.action}</strong>{" "}
              this deposit?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirm}>Cancel</Button>
            <Button
              variant="contained"
              color={confirmAction?.action === "approve" ? "success" : "error"}
              onClick={performAction}
              disabled={actionBusy}
            >
              {actionBusy
                ? "Processing..."
                : confirmAction?.action === "approve"
                ? "Approve"
                : "Reject"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
